import { LinkedContact } from "@/Common/Api/Patient/Dto/LinkedContact";
import { ProfileTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { Relative } from "../Models/Relative";
import { relativeStore } from "./RelativeStore";

/**
 * Flux actions to handle relatives.
 */
class RelativeActions extends BaseAction {
    /**
     * Switch tabs in the relative file.
     * @param tab the tab to switch.
     * @param isEditing boolean to determine whether the tab will be displayed in edit mode or not.
     */
    public switchTab(tab: ProfileTab, isEditing: boolean): void {
        const event = new LocalEvent(LocalOperations.Relative.SwitchProfileTab);
        event.AddItem("tab", tab);
        event.AddItem("isEditing", isEditing);
        LocalEventDispatcher.dispatch(event);
    }

    /**
     * Close the current relative file.
     * @param redirect redirect after closing.
     */
    public closeFile(redirect: boolean): void {
        const event = new LocalEvent(LocalOperations.Relative.CloseFile);
        event.AddItem("redirect", redirect);
        LocalEventDispatcher.dispatch(event);
    }

    /**
     * Load a relative file (patient or relative) from the API.
     * @param contactId the person identifier.
     * @param forceRefresh if we reload when the relative is already loaded.
     * @param subOperation an optionnal operation that should execute after the file is loaded. Open the file by default.
     */
    public loadFile(contactId: string, forceRefresh: boolean = false, subOperation: SubServerOperations.Relative = SubServerOperations.Relative.LoadAndOpenFile): void {
        if (relativeStore.relatives[contactId] && !forceRefresh) {
            if (subOperation === SubServerOperations.Relative.LoadAndOpenFile)
                sessionStore.dispatch(LocalOperations.Person.OpenFile, { person: relativeStore.relatives[contactId] });

            return;
        }

        const requestData = {
            IndividualId: contactId,
            IncludeRelationships: true
        };
        this.Execute(ServerOperations.Server.Myle_LoadIndividual, requestData, subOperation);
    }

    /**
     * Open a relative file already loaded.
     * @param contactId the person identifier.
     */
    public openFile(contactId: string): void {
        const event = new LocalEvent(LocalOperations.Relative.OpenFile);
        event.AddItem("contactId", contactId);
        LocalEventDispatcher.dispatch(event);
    }

    /**
     * Update the demographic data of a relative.
     * @param relative the relative.
     */
    public updateDemographics(relative: Relative): void {
        const requestData = {
            ContactId: relative.ContactId,
            FirstName: relative.FirstName,
            LastName: relative.LastName,
            PreferredName: relative.PreferredName,
            PreferredPronouns: relative.PreferredPronouns,
            SpecifiedPreferredPronouns: relative.SpecifiedPreferredPronouns,
            DateOfBirth: relative.DateOfBirth?.toDateOnly(),
            PrefixLookupId: relative.PrefixLookupId,
            GenderIdentity: relative.GenderIdentity,
            GenderLookupId: relative.GenderLookupId,
            EthnicityKeys: []
        };
        this.Execute(ServerOperations.Server.Myle_Individual_Demographics_Update, requestData);
    }

    /**
     * Update the contact data of a relative.
     * @param relative the relative.
     */
    public updateContacts(relative: Relative): void {
        const requestData = {
            IndividualContacts: {
                ContactId: relative.ContactId,
                ContactFacetId: relative.ContactFacetId,
                PhoneNumbers: relative.Phones,
                Addresses: relative.Addresses,
                Emails: relative.Emails,
                ContactPoints: relative.ContactPoints,
                DoNotEmail: relative.DoNotEmail
            }
        };
        this.Execute(ServerOperations.Server.Myle_Individual_Contacts_Update, requestData);
    }

    /**
     * Update the identifier data of a relative.
     * @param relative the relative.
     */
    public updateIdentifiers(relative: Relative): void {
        const requestData = {
            ContactId: relative.ContactId,
            ContactFacetId: relative.ContactFacetId,
            HealthCards: relative.HealthCards,
            OtherIdentifiers: relative.OtherPatientIdentifiers
        };
        this.Execute(ServerOperations.Server.Myle_Individual_Identifiers_Update, requestData);
    }

    /**
     * Update the relations of a relative.
     * @param relations the relations.
     */
    public updateRelations(relations: LinkedContact[]): void {
        const relative = relativeStore.GetCurrent();
        const deletedRelationships = relative.Relationships.filter(r => !relations.any(p => p.Id == r.Id));

        this.Execute(ServerOperations.Server.Myle_Individual_Relationships_Update, {
            ContactId: relative.ContactId,
            Relationships: relations,
            DeleteRelationshipIds: deletedRelationships.map(r => r.Id)
        });
    }

    /**
     * Convert a relative to patient.
     * @param relative the relative to be converted to patient.
     */
    public convertToPatient(relative: Relative): void {
        const requestData = {
            ContactFacetId: relative.ContactFacetId
        };
        this.Execute(ServerOperations.Server.Myle_ConvertRelativeToPatient, requestData);
    }

    /**
     * Save or Delete a memo for a relative file.
     * @param newMemo the content of the memo.
     * @param contactId the contactId of the relative to which the memo will be attached to.
     * @param isDeletion determines whether a note will be created or deleted. Create a memo by default.
     */
    public saveMemo(newMemo: string, contactId: string, isDeletion: boolean = false): void {
        const requestData = {
            PatientContactId: contactId,
            MemoText: newMemo,
            IsDeletion: isDeletion
        };

        this.Execute(ServerOperations.Server.Myle_Patient_Memo_Save, requestData);
    }

    /**
     * Mark as read a memo when opening a relative file.
     */
    public markAsReadProfileMemo(): void {
        sessionStore.dispatch(LocalOperations.Relative.MarkAsReadProfileMemo);
    }

    /**
     * Deactivates a relative.
     * @param contactId the person identifier.
     * @param reasonId the reason of the deactivation.
     * @param note the noted related to the deactivation.
     */
    public deactivateRelative(contactId: string, reasonId: string, note: string): void {
        if (contactId && reasonId) {
            const requestData = {
                PatientContactId: contactId,
                ReasonForInactivityLookupId: reasonId,
                DeactivationNote: note
            };

            this.Execute(ServerOperations.Server.Myle_Patient_Deactivate, requestData);
        }
    }

    /**
     * Reactivates a relative.
     * @param contactId the person identifier.
     */
    public reactivateRelative(contactId: string): void {
        if (contactId) {
            const requestData = {
                PatientContactId: contactId
            };

            this.Execute(ServerOperations.Server.Myle_Patient_Reactivate, requestData);
        }
    }
}

export { RelativeActions };
